// =================================================================
// Engine: Hartford Nomogram module (PSD §4.2.6 / TODO Task 1.3, 3.5)
// Pure functions — no framework imports.
// =================================================================
import { IntervalRecommendation, NomogramSource } from '@amino-pk/shared-types';
import { err, ok } from './types';
import { HARTFORD_WINDOW_HR } from './constants';
import { HARTFORD_BOUNDARIES, ALTERNATIVE_BOUNDARIES, } from './nomogramBoundaries';
export { HARTFORD_BOUNDARIES, ALTERNATIVE_BOUNDARIES };
/** Linear interpolation of a boundary line at a given time. */
export function interpolateBoundary(line, timeHr) {
    if (timeHr <= line[0][0])
        return line[0][1];
    const last = line[line.length - 1];
    if (timeHr >= last[0])
        return last[1];
    for (let i = 0; i < line.length - 1; i++) {
        const [t0, y0] = line[i];
        const [t1, y1] = line[i + 1];
        if (timeHr >= t0 && timeHr <= t1) {
            const frac = (timeHr - t0) / (t1 - t0);
            return y0 + frac * (y1 - y0);
        }
    }
    return last[1];
}
/** Validate that a draw time is within the Hartford 6–14 h window (inclusive). */
export function validateSamplingWindow(drawTimeHr, window = HARTFORD_WINDOW_HR) {
    return drawTimeHr >= window[0] && drawTimeHr <= window[1];
}
/**
 * Classify a Hartford random level (PSD §4.2.6).
 * Out-of-window → interval null. Exact boundary → longer interval (conservative).
 */
export function classifyHartford(levelMg, drawTimeHr) {
    if (!(levelMg > 0)) {
        return err('level-must-be-positive', 'Level must be positive.', 'levelMg');
    }
    if (!validateSamplingWindow(drawTimeHr)) {
        return ok({
            interval: null,
            inWindow: false,
            reason: `Level drawn at ${drawTimeHr}h — outside the 6–14h Hartford window.`,
        });
    }
    const q48Exit = interpolateBoundary(HARTFORD_BOUNDARIES.q48Exit, drawTimeHr);
    const q36q48 = interpolateBoundary(HARTFORD_BOUNDARIES.q36q48, drawTimeHr);
    const q24q36 = interpolateBoundary(HARTFORD_BOUNDARIES.q24q36, drawTimeHr);
    if (levelMg >= q48Exit) {
        return ok({ interval: IntervalRecommendation.ExitExtendedInterval, inWindow: true, reason: 'Above q48/exit boundary — exit extended-interval.' });
    }
    if (levelMg >= q36q48) {
        return ok({ interval: IntervalRecommendation.Q48h, inWindow: true, reason: 'q48h zone.' });
    }
    if (levelMg >= q24q36) {
        return ok({ interval: IntervalRecommendation.Q36h, inWindow: true, reason: 'q36h zone.' });
    }
    return ok({ interval: IntervalRecommendation.Q24h, inWindow: true, reason: 'q24h zone.' });
}
/** Map a Hartford IntervalRecommendation to hours. Exit → 0 (route to traditional). */
export function intervalToHours(interval) {
    switch (interval) {
        case IntervalRecommendation.Q24h:
            return 24;
        case IntervalRecommendation.Q36h:
            return 36;
        case IntervalRecommendation.Q48h:
            return 48;
        case IntervalRecommendation.Q12h:
            return 12;
        case IntervalRecommendation.Hold:
            return 0;
        case IntervalRecommendation.ExitExtendedInterval:
            return 0;
        default:
            return 0;
    }
}
// ── Alternative nomograms (Phase 2, TODO Task 2.3) ──────
function getBoundaries(source) {
    if (source === NomogramSource.Hartford)
        return HARTFORD_BOUNDARIES;
    return ALTERNATIVE_BOUNDARIES[source];
}
/** Classify a level against any nomogram source (Phase 2, TODO Task 2.3). */
export function classifyAlternative(source, levelMg, drawTimeHr, window = HARTFORD_WINDOW_HR) {
    if (!(levelMg > 0)) {
        return err('level-must-be-positive', 'Level must be positive.', 'levelMg');
    }
    if (drawTimeHr < window[0] || drawTimeHr > window[1]) {
        return ok({ interval: null, inWindow: false, reason: `Outside ${source} window.` });
    }
    const b = getBoundaries(source);
    const q48Exit = interpolateBoundary(b.q48Exit, drawTimeHr);
    const q36q48 = interpolateBoundary(b.q36q48, drawTimeHr);
    const q24q36 = interpolateBoundary(b.q24q36, drawTimeHr);
    if (levelMg >= q48Exit) {
        return ok({ interval: IntervalRecommendation.ExitExtendedInterval, inWindow: true, reason: `${source}: exit.` });
    }
    if (levelMg >= q36q48) {
        return ok({ interval: IntervalRecommendation.Q48h, inWindow: true, reason: `${source}: q48h.` });
    }
    if (levelMg >= q24q36) {
        return ok({ interval: IntervalRecommendation.Q36h, inWindow: true, reason: `${source}: q36h.` });
    }
    return ok({ interval: IntervalRecommendation.Q24h, inWindow: true, reason: `${source}: q24h.` });
}
// ── Extended-interval dose (PSD §4.2.5) ──────
/** Extended-interval dose = eiDoseMgKg × dosingWeight. */
export function computeEIDose(eiDoseMgKg, dosingWeightKg) {
    return eiDoseMgKg * dosingWeightKg;
}
//# sourceMappingURL=nomogram.js.map